// Single source of truth for "is this box online?" calculation.
//
// Used by the dashboard hero, the map, and (eventually) the sensors
// list so totals are always consistent across surfaces.

const ONLINE_WINDOW_MS = 5 * 60 * 1000;

export interface PresenceInput {
  firmware_protocol?: 'ws' | 'http' | string | null;
  last_seen_at?: string | Date | null;
}

/**
 * Rule (unified for both protocols):
 *  - Online iff last_seen_at is within the last 5 minutes.
 *
 * Why one rule fits both:
 *  - WS box     : gateway writes last_seen_at on every app heartbeat
 *                 (also on alarm_event / command_ack, per PR #19 of
 *                 chave-cmb-ws-gateway).
 *  - Polling box: every GET /control/command-front/:bluetooth call
 *                 stamps last_seen_at on the API (chave-cmb-api PR
 *                 #176). The polling loop itself IS the heartbeat.
 *
 * Boxes with no protocol set or no last_seen_at at all (never
 * connected since the column was added) return false. The map filter
 * + the dashboard offline count handle that by listing them as
 * offline — which is technically true since we have no evidence
 * they're reachable.
 */
export function isBoxOnline(box: PresenceInput, nowMs: number = Date.now()): boolean {
  if (!box.last_seen_at) return false;
  const seenMs = new Date(box.last_seen_at).getTime();
  if (isNaN(seenMs)) return false;
  return nowMs - seenMs < ONLINE_WINDOW_MS;
}
